/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import { Meteor } from 'meteor/meteor';
import { get, isEqual, isEmpty, isNil, hasIn, includes } from 'lodash';
import PropTypes from 'prop-types';
import { Bert } from 'meteor/themeteorchef:bert';
import { Alert } from 'react-bootstrap';

import * as layerOps from '../../../api/Layers/layer-operations';
import { GEOJSON_API_BASED_SUBTYPES, LAYER_SUBTYPES } from '../../../api/Layers/Layers';
import { styleFetchOne } from '../../../api/Styles/style-methods-client';

import {
  featurePoint2LatLng,
  featureGetZoom,
  latlong2featurePoint,
  featureSetZoom,
  featureSetBBox
} from '../../../modules/geojson-utilities';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';
import getDefaultStyle from '../../../modules/get-default-style';

import LeafletMap from '../../components/LeafletMap';
import ButtonOnMap from '../ButtonOnMap';

let addDefaultBaseMap;
let addOnlineBaseMap;
let customCollapseButton;
let customHomeButton;
let customMapsAttribution;
let customReloadButton;
let customScale;
let customZoom;
let drawSingleLayer;
let geojsonLegend;
let getBBoxFromCoordsAndZoom;
let getMapCenter;
let Geocoding;

class MapContainerViewLayer extends Component {
  state = {
    clientModulesReady: false,
    isLeafletMapLoaded: get(this, 'layerMapComponent.current.state.isMapLoaded', false),
    isLayerMapLoaded: false,
    hasErrors: false
  };

  layerMapComponent = React.createRef();

  layerMap = null;

  layerToDraw = null;

  componentDidMount() {
    const { clientModulesReady } = this.state;
    if (!clientModulesReady) {
      this.loadClientModules();
    }
  }

  async componentDidUpdate(prevProps) {
    const { clientModulesReady, isLeafletMapLoaded, isLayerMapLoaded } = this.state;
    const { layer, defaultBaseMap } = this.props;
    const { layer: prevLayer, defaultBaseMap: prevDefaultBaseMap } = prevProps;
    if (clientModulesReady && isLeafletMapLoaded && !isLayerMapLoaded) {
      this.initializeMap();
    }
    if (
      isLayerMapLoaded &&
      !isEmpty(defaultBaseMap) &&
      !isEqual(prevDefaultBaseMap, defaultBaseMap)
    ) {
      await addDefaultBaseMap({
        defaultBaseMap,
        leafletMap: this.layerMap,
        isUpdate: true
      });
    }
    if (isLayerMapLoaded && !isEqual(prevLayer, layer)) {
      const style = (await styleFetchOne(layerOps.getStyleId(layer))) || getDefaultStyle(layer);
      this.layerToDraw = await drawSingleLayer({
        layer,
        style,
        leafletMap: this.layerMap,
        layerToDraw: this.layerToDraw,
        isUpdate: true,
        zoomLevel: true
      });
    }
    if (!isEqual(layerOps.getMapLocation(prevLayer), layerOps.getMapLocation(layer))) {
      if (hasIn(this, ['layerMap', 'homeButton'])) {
        this.layerMap.removeControl(this.layerMap.homeButton);
        customHomeButton(this.layerMap, layerOps.getMapLocation(layer));
      }
    }
    this.createMapCeneter();
  }

  componentWillUnmount() {
    const { layer } = this.props;
    if (layerOps.isRealTime(layer) || layerOps.isAnnotationsRealTime(layer)) {
      this.layerToDraw.stop();
    }
  }

  reloadLayer = async () => {
    const { layer } = this.props;
    const style = (await styleFetchOne(layerOps.getStyleId(layer))) || getDefaultStyle(layer);
    this.layerToDraw = await drawSingleLayer({
      layer,
      style,
      leafletMap: this.layerMap,
      layerToDraw: this.layerToDraw,
      isUpdate: true,
      zoomLevel: true
    });
  };

  updateMapState = newState => {
    this.setState({ isLeafletMapLoaded: newState });
  };

  updateMapCenter = async () => {
    const { isLayerMapLoaded } = this.state;
    const { layer } = this.props;
    if (isLayerMapLoaded) {
      const currentMapCenter = latlong2featurePoint(this.layerMap.getCenter().wrap());
      featureSetZoom(currentMapCenter, this.layerMap.getZoom());
      featureSetBBox(
        currentMapCenter,
        getBBoxFromCoordsAndZoom({ map: this.layerMap, mapLocation: currentMapCenter })
      );
      try {
        await methodPromise('layers.updateMapLocation', layerOps.getId(layer), currentMapCenter);
        Bert.alert('Layer Updated!', 'success');
      } catch (error) {
        handleErrorInClient(error);
      }
    } else {
      Bert.alert('Layer Not Loaded!', 'warning');
    }
  };

  createMapCeneter = async () => {
    const { isLayerMapLoaded } = this.state;
    const { layer } = this.props;
    if (isLayerMapLoaded) {
      if (isEmpty(layerOps.getMapLocation(layer))) this.updateMapCenter();
    }
  };

  initializeMap = async () => {
    const { defaultBaseMap, layer, userControlSetting } = this.props;
    let layerLocation = layerOps.getMapLocation(layer);
    const newSearchControl = Geocoding.geosearch({ useMapBounds: false });
    if (isEmpty(layerLocation)) {
      layerLocation = userControlSetting.defaultMapCenter;
    }
    this.layerMap = get(this, 'layerMapComponent.current.leafletMap');
    this.layerMap.setView(
      featurePoint2LatLng(getMapCenter({ location: layerLocation })),
      featureGetZoom(layerLocation)
    );
    if (isEmpty(defaultBaseMap)) {
      addOnlineBaseMap(this.layerMap);
    } else {
      await addDefaultBaseMap({
        defaultBaseMap,
        leafletMap: this.layerMap
      });
    }
    const style = (await styleFetchOne(layerOps.getStyleId(layer))) || getDefaultStyle(layer);
    this.layerToDraw = await drawSingleLayer({
      layer,
      style,
      leafletMap: this.layerMap,
      layerToDraw: this.layerToDraw,
      zoomLevel: true
    });

    if (isNil(this.layerToDraw)) this.setState({ hasErrors: true });

    if (
      includes(
        [
          ...GEOJSON_API_BASED_SUBTYPES,
          LAYER_SUBTYPES.GEOJSONVT,
          LAYER_SUBTYPES.MAPBOX,
          LAYER_SUBTYPES.REALTIME
        ],
        layerOps.getSubType(layer)
      )
    ) {
      if (
        includes([...GEOJSON_API_BASED_SUBTYPES], layerOps.getSubType(layer)) &&
        isEqual(layerLocation, userControlSetting.defaultMapCenter)
      ) {
        this.layerMap.fitBounds(this.layerToDraw.getBounds());
      }
      if (
        layerOps.isRealTime(layer) ||
        (layerOps.isAnnotationsRealTime(layer) &&
          isEqual(layerLocation, userControlSetting.defaultMapCenter))
      ) {
        this.layerToDraw.once('update', () => {
          this.layerMap.fitBounds(this.layerToDraw.getBounds());
        });
      }
      geojsonLegend(this.layerMap, style);
    }
    if (this.layerMap.attributionControl) {
      this.layerMap.removeControl(this.layerMap.attributionControl);
    }
    if (this.layerMap.zoomControl) this.layerMap.removeControl(this.layerMap.zoomControl);
    if (this.layerMap.collapseButton) this.layerMap.removeControl(this.layerMap.collapseButton);
    customCollapseButton(this.layerMap);
    customZoom(this.layerMap);
    this.layerMap.addControl(newSearchControl);
    this.layerMap.searchControl = newSearchControl;
    customReloadButton(this.layerMap, this.reloadLayer);
    customHomeButton(this.layerMap, layerLocation);
    customMapsAttribution(this.layerMap);
    customScale(this.layerMap);
    this.setState({ isLayerMapLoaded: true });
  };

  async loadClientModules() {
    if (Meteor.isClient) {
      ({
        addDefaultBaseMap,
        addOnlineBaseMap,
        customCollapseButton,
        customHomeButton,
        customMapsAttribution,
        customReloadButton,
        customScale,
        customZoom,
        drawSingleLayer,
        geojsonLegend,
        getBBoxFromCoordsAndZoom,
        getMapCenter
      } = await import('../../../modules/leaflet-operations'));
      Geocoding = await import('esri-leaflet-geocoder');
      this.setState({ clientModulesReady: true });
    }
  }

  render() {
    const { deployEnv, layer } = this.props;
    const { hasErrors } = this.state;

    if (hasErrors)
      return <Alert variant="warning">No está disponible para este tipo de layer</Alert>;

    return (
      <>
        <LeafletMap
          mapId="layerMap"
          headerType="1"
          ref={this.layerMapComponent}
          updateMapState={this.updateMapState}
          deployEnv={deployEnv}
          {...this.props}
        />
        <ButtonOnMap
          numberOfButtonsRight={
            layerOps.isGeoJSONApiBased(layer) ||
            layerOps.isGeoJSONVT(layer) ||
            layerOps.isRealTime(layer) ||
            layerOps.isMapBox(layer) ||
            layerOps.isAnnotationsRealTime(layer)
              ? 1
              : 0
          }
        >
          <div
            onClick={this.updateMapCenter}
            title="Reload initial map position"
            role="button"
            onKeyPress={() => {}}
            tabIndex={0}
          >
            <img
              src="/maps-buttons/flecha_ubicacion.svg"
              style={{ height: '34px', width: '34px', cursor: 'pointer' }}
              alt="Update Map Center"
            />
          </div>
        </ButtonOnMap>
      </>
    );
  }
}

MapContainerViewLayer.propTypes = {
  defaultBaseMap: PropTypes.object.isRequired,
  deployEnv: PropTypes.string.isRequired,
  layer: PropTypes.object.isRequired,
  userControlSetting: PropTypes.object.isRequired
};

export default MapContainerViewLayer;
